import React, { useState } from "react";
import type {
    DateOnlyRange,
    DatePickerSelectionMode,
    DatePickerVariant,
} from "@design-system/DatePicker/DatePicker.types";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { BooleanControl, PlaygroundSelect } from "./playgroundControls";
import { DatePickerPlaygroundPicker } from "./DatePickerPlaygroundPicker";
import {
    datePickerVariants,
    getAvailableModes,
    getDatePickerOutput,
} from "./DatePickerPlayground.lib";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./DatePickerPlayground.module.scss";

const visibleMonthOptions: ReadonlyArray<{
    value: 1 | 2;
    label: string;
}> = [
    { value: 1, label: "One" },
    { value: 2, label: "Two" },
];

export const DatePickerPlayground = () => {
    const [mode, setMode] = useState<DatePickerSelectionMode>("day");
    const [variant, setVariant] = useState<DatePickerVariant>("button");
    const [singleValue, setSingleValue] = useState<string>();
    const [rangeValue, setRangeValue] = useState<DateOnlyRange>();
    const [weekValue, setWeekValue] = useState<DateOnlyRange>();
    const [monthValue, setMonthValue] = useState<DateOnlyRange>();
    const [monthRangeValue, setMonthRangeValue] = useState<DateOnlyRange>();
    const [quarterValue, setQuarterValue] = useState<DateOnlyRange>();
    const [yearValue, setYearValue] = useState<DateOnlyRange>();
    const [numberOfMonths, setNumberOfMonths] = useState<1 | 2>(1);
    const [showPresets, setShowPresets] = useState(false);
    const [pinnablePresets, setPinnablePresets] = useState(false);
    const [showActions, setShowActions] = useState(false);
    const [showTodayButton, setShowTodayButton] = useState(true);
    const [showWeekNumbers, setShowWeekNumbers] = useState(false);
    const [showOutsideDays, setShowOutsideDays] = useState(true);
    const [fixedWeeks, setFixedWeeks] = useState(true);
    const [isPopoverSizeFixed, setIsPopoverSizeFixed] = useState(true);
    const [disableWeekends, setDisableWeekends] = useState(false);
    const [required, setRequired] = useState(false);
    const [clearable, setClearable] = useState(true);
    const [isInvalid, setIsInvalid] = useState(false);
    const [showPeriodSwitcher, setShowPeriodSwitcher] = useState(false);
    const availableModes = getAvailableModes(
        mode,
        showPeriodSwitcher ? "multiple" : "single",
    );
    const isPeriodSelector =
        mode === "month" ||
        mode === "monthRange" ||
        mode === "quarter" ||
        mode === "year";

    const sharedProps = {
        variant,
        label: "Date",
        triggerLabel: "Choose date",
        numberOfMonths,
        showPresets,
        pinnablePresets,
        showActions,
        showTodayButton,
        showWeekNumbers,
        showOutsideDays,
        fixedWeeks,
        isPopoverSizeFixed,
        required,
        clearable,
        invalid: isInvalid,
        availableModes,
        onModeChange: setMode,
        disabledDates: disableWeekends ? { dayOfWeek: [0, 6] } : undefined,
    };

    const output = getDatePickerOutput({
        mode,
        dayValue: singleValue,
        rangeValue,
        weekValue,
        monthValue,
        monthRangeValue,
        quarterValue,
        yearValue,
    });

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Selector</h2>
                        <ToggleGroup.Root
                            value={isPeriodSelector ? "period" : "date"}
                            onValueChange={(value) => {
                                if (value === "date") {
                                    setMode("day");
                                }
                                if (value === "period") {
                                    setMode("month");
                                }
                            }}
                        >
                            <ToggleGroup.Item value="date">
                                Date
                            </ToggleGroup.Item>
                            <ToggleGroup.Item value="period">
                                Period
                            </ToggleGroup.Item>
                        </ToggleGroup.Root>
                        <h2 className={styles.subheading}>Selection</h2>
                        <ToggleGroup.Root
                            value={mode}
                            onValueChange={(value) => {
                                if (value) {
                                    setMode(value);
                                }
                            }}
                        >
                            {isPeriodSelector ? (
                                <>
                                    <ToggleGroup.Item value="month">
                                        Month
                                    </ToggleGroup.Item>
                                    <ToggleGroup.Item value="monthRange">
                                        Month range
                                    </ToggleGroup.Item>
                                    <ToggleGroup.Item value="quarter">
                                        Quarter
                                    </ToggleGroup.Item>
                                    <ToggleGroup.Item value="year">
                                        Year
                                    </ToggleGroup.Item>
                                </>
                            ) : (
                                <>
                                    <ToggleGroup.Item value="day">
                                        Day
                                    </ToggleGroup.Item>
                                    <ToggleGroup.Item value="range">
                                        Range
                                    </ToggleGroup.Item>
                                    <ToggleGroup.Item value="week">
                                        Week
                                    </ToggleGroup.Item>
                                </>
                            )}
                        </ToggleGroup.Root>
                    </section>

                    <section className={styles.fields}>
                        <div className={styles.field}>
                            <span>Variant</span>
                            <PlaygroundSelect<DatePickerVariant>
                                options={datePickerVariants}
                                value={variant}
                                onChange={setVariant}
                            />
                        </div>
                        <div className={styles.field}>
                            <span>Visible months</span>
                            <PlaygroundSelect<1 | 2>
                                options={visibleMonthOptions}
                                value={numberOfMonths}
                                onChange={setNumberOfMonths}
                            />
                        </div>
                    </section>

                    <section>
                        <h2>Features</h2>
                        <div className={styles.booleanGrid}>
                            <BooleanControl
                                label={
                                    isPeriodSelector
                                        ? "Period type switcher"
                                        : "Date mode switcher"
                                }
                                checked={showPeriodSwitcher}
                                onChange={setShowPeriodSwitcher}
                            />
                            <BooleanControl
                                label="Quick presets"
                                checked={showPresets}
                                onChange={setShowPresets}
                            />
                            {showPresets && (
                                <BooleanControl
                                    label="Pinnable presets"
                                    checked={pinnablePresets}
                                    onChange={setPinnablePresets}
                                />
                            )}
                            <BooleanControl
                                label="Cancel / Apply"
                                checked={showActions}
                                onChange={setShowActions}
                            />
                            <BooleanControl
                                label="Today shortcut"
                                checked={showTodayButton}
                                onChange={setShowTodayButton}
                            />
                            <BooleanControl
                                label="Week numbers"
                                checked={showWeekNumbers}
                                onChange={setShowWeekNumbers}
                            />
                            <BooleanControl
                                label="Outside days"
                                checked={showOutsideDays}
                                onChange={setShowOutsideDays}
                            />
                            <BooleanControl
                                label="Fixed six weeks"
                                checked={fixedWeeks}
                                onChange={setFixedWeeks}
                            />
                            {!isPeriodSelector && (
                                <BooleanControl
                                    label="Fixed popover size"
                                    checked={isPopoverSizeFixed}
                                    onChange={setIsPopoverSizeFixed}
                                />
                            )}
                            <BooleanControl
                                label="Disable weekends"
                                checked={disableWeekends}
                                onChange={setDisableWeekends}
                            />
                            <BooleanControl
                                label="Required"
                                checked={required}
                                onChange={setRequired}
                            />
                            <BooleanControl
                                label="Clearable"
                                checked={clearable}
                                onChange={setClearable}
                            />
                            <BooleanControl
                                label="Invalid"
                                checked={isInvalid}
                                onChange={setIsInvalid}
                            />
                        </div>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={
                    <>
                        {mode} · {variant}
                    </>
                }
                output={JSON.stringify(output, null, 2)}
            >
                <DatePickerPlaygroundPicker
                    mode={mode}
                    sharedProps={sharedProps}
                    dayValue={singleValue}
                    rangeValue={rangeValue}
                    weekValue={weekValue}
                    monthValue={monthValue}
                    monthRangeValue={monthRangeValue}
                    quarterValue={quarterValue}
                    yearValue={yearValue}
                    onDayChange={setSingleValue}
                    onRangeChange={setRangeValue}
                    onWeekChange={setWeekValue}
                    onMonthChange={setMonthValue}
                    onMonthRangeChange={setMonthRangeValue}
                    onQuarterChange={setQuarterValue}
                    onYearChange={setYearValue}
                />
            </ShowcasePreview>
        </SandboxLayout>
    );
};
